import { Button } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { DataTable } from '@/components/Ui/DataTable';
import { LedgerRow } from '@/components/Ui/LedgerRow';
import { Section } from '@/components/Ui/Section';
import { SegmentedButtons } from '@/components/Ui/SegmentedButtons';
import { SwitchRow } from '@/components/Ui/SwitchRow';
import { UiLink } from '@/components/Ui/UiLink';
import { useLanguageAwareRouting } from '@/hooks/useLanguageAwareRouting';
import { hasFields, hasFileField } from '@/lib/form-site';
import { dumpFormSpecToToml, type FormField, isTrapField } from '@/lib/form-specs';
import classes from './FormOverview.module.css';
import { failureRuleSummary } from './riskItems';
import type { SectionProps } from './types';

type FieldRow = { key: string; field: FormField };

function onFail(field: FormField): 'reject' | 'spam' | 'trash' | 'pass' {
  return field.on_fail ?? 'pass';
}

export function FieldsSection({
  form,
  save,
  data,
  spec,
}: Omit<SectionProps, 'method' | 'hasMessages'>) {
  const { t } = useTranslation();
  const { createLanguageURL } = useLanguageAwareRouting();
  const fields = hasFields(spec);
  const rows: FieldRow[] = fields
    ? Object.entries(spec.fields).map(([key, field]) => ({ key, field }))
    : [];
  const dropped = !!spec?.settings?.discard_additional_fields;
  const editorUrl = createLanguageURL(`/account/forms/${form.id}/fields`);

  const rejection = (reason: string) => data.rejections?.find((r) => r.reason === reason);
  const rejected = rejection('field_rules');
  const trashed = rejection('trashed');

  const setUnlisted = (drop: boolean, close: () => void) => {
    close();
    if (!spec || drop === dropped) return;
    const next = dumpFormSpecToToml({
      ...spec,
      settings: { ...spec.settings, discard_additional_fields: drop },
    });
    void save(
      { specs: next },
      t(drop ? 'form.fields.unlistedDroppedSaved' : 'form.fields.unlistedKeptSaved')
    );
  };

  const ruleParts = failureRuleSummary(t, spec);
  const rulesValue = ruleParts.length > 0 ? ruleParts.join(', ') : t('form.fields.rulesNone');

  const rejectedParts = [
    rejected?.count
      ? t('form.fields.rejectedCount', {
          count: rejected.count,
          detail: rejected.top_detail
            ? t(
                rejected.count === 1
                  ? 'form.fields.rejectedDetail'
                  : 'form.fields.rejectedDetailMost',
                { field: rejected.top_detail }
              )
            : '',
        })
      : '',
    trashed?.count ? t('form.fields.trashedCount', { count: trashed.count }) : '',
  ].filter(Boolean);

  const fileAccepted = !fields || hasFileField(spec);

  return (
    <Section
      id="fields"
      title={t('form.fields.title')}
      actions={
        <Button component={Link} to={editorUrl} size="xs">
          {fields ? t('form.fields.edit') : t('form.fields.add')}
        </Button>
      }
    >
      {fields ? (
        <DataTable<FieldRow>
          caption={t('form.fields.title')}
          rows={rows}
          rowKey={(r) => r.key}
          columns={[
            {
              key: 'name',
              header: t('form.fields.colField'),
              render: (r) => <span className={classes.mono}>{r.key}</span>,
            },
            {
              key: 'type',
              header: t('form.fields.colType'),
              render: (r) =>
                isTrapField(r.field)
                  ? t('fields.trapCardLabel')
                  : t(`form.fieldType.${r.field.is_email ? 'email' : r.field.field}`, {
                      defaultValue: r.field.field,
                    }),
            },
            {
              key: 'required',
              header: t('form.fields.colRequired'),
              render: (r) =>
                r.field.required ? t('form.fields.required') : t('form.fields.optional'),
            },
            {
              key: 'onFail',
              header: t('form.fields.colInvalid'),
              render: (r) => t(`fields.rule.${onFail(r.field)}`),
            },
          ]}
        />
      ) : (
        <LedgerRow
          label={t('form.fields.title')}
          value={t('form.fields.none')}
          help={t('form.fields.noneHelp')}
        />
      )}
      {fields ? (
        <SwitchRow
          label={t('form.fields.validate')}
          checked={form.check_specs}
          onChange={(next) =>
            void save(
              { check_specs: next },
              t(next ? 'ui.turnedOn' : 'ui.turnedOff', { name: t('form.fields.validate') })
            )
          }
          help={form.check_specs ? t('form.fields.validateHelp') : undefined}
          risk={form.check_specs ? undefined : t('form.fields.validateOffRisk')}
          changed={
            form.check_specs
              ? undefined
              : {
                  defaultLabel: t('ui.on').toLowerCase(),
                  onReset: () =>
                    void save(
                      { check_specs: true },
                      t('ui.turnedOn', { name: t('form.fields.validate') })
                    ),
                }
          }
        />
      ) : (
        <LedgerRow label={t('form.fields.validate')} value={t('form.fields.validateNoFields')} />
      )}
      <LedgerRow
        label={t('form.fields.unlisted')}
        value={dropped ? t('form.fields.unlistedDropped') : t('form.fields.unlistedKept')}
        help={dropped ? t('form.fields.unlistedDroppedHelp') : t('form.fields.unlistedKeptHelp')}
        changed={
          dropped
            ? {
                defaultLabel: t('form.fields.unlistedKept').toLowerCase(),
                onReset: () => setUnlisted(false, () => undefined),
              }
            : undefined
        }
        editor={
          fields
            ? {
                render: (close) => (
                  <SegmentedButtons
                    aria-label={t('form.fields.unlisted')}
                    options={[
                      { value: 'kept', label: t('form.fields.unlistedKept') },
                      { value: 'dropped', label: t('form.fields.unlistedDropped') },
                    ]}
                    value={dropped ? 'dropped' : 'kept'}
                    onChange={(v) => setUnlisted(v === 'dropped', close)}
                  />
                ),
              }
            : undefined
        }
      />
      <LedgerRow
        label={t('form.fields.uploads')}
        value={fileAccepted ? t('form.fields.uploadsAccepted') : t('form.fields.uploadsRejected')}
        help={
          fileAccepted ? t('form.fields.uploadsAcceptedHelp') : t('form.fields.uploadsRejectedHelp')
        }
      />
      {fields && (
        <LedgerRow
          label={t('form.fields.whenInvalid')}
          value={rulesValue}
          help={
            <>
              {t('form.fields.whenInvalidHelp')}{' '}
              {rejectedParts.length > 0 &&
                `${t('form.fields.thisWeek', { list: rejectedParts.join(', ') })} `}
              <UiLink to={`/account/forms/${form.id}/messages`}>
                {t('form.fields.seeRejected')}
              </UiLink>
            </>
          }
        />
      )}
    </Section>
  );
}
